/**
 * IMPORTANT! Do not use any services/composables here! (other than the ones defined in the context).
 *
 * This redirect handler should be used solely to determine whether user should be redirected
 * to a certain place or not, based on the application state, which is passed into the exported
 * function as first argument.
 *
 * Only imports that help to determine the redirect are allowed here (like consts or moment).
 */

import { is } from 'ramda'

import { useRedirection } from '../redirection'

import { replaceWithResolvedRoute } from '../utils/replace-with-resolved-route'
import { removeLeadingSlash } from '../utils/remove-leading-slash'

import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import type { RedirectContext, RedirectionPayload } from '../types'

export const targetUrlRedirect = ({ route, onboardingFinished, store, router, logger, defaultRedirect }: RedirectContext): unknown => {
  if (!route) return

  let r: RedirectionPayload | undefined

  // original redirection url
  if (is(Object, route.params.redirection)) {
    r = route.params.redirection
    delete route.params.redirection
  }

  if (r && (r.name || (r.path != null && removeLeadingSlash(r.path)))) {
    r.params = r.params || {}

    if (!r.params.businessId) {
      r.params.businessId = store.getters['business/businessId']
    }

    let location: RouteLocationRaw = r

    if (r.path != null && removeLeadingSlash(r.path) && !r.name) {
      // Path-based navigation ignores `params`; businessId is already in the path.
      location = router.resolve({
        path: r.path,
        ...(r.query != null ? { query: r.query } : {}),
      })
    }

    // shortcut: redirect to original url
    return (async (): Promise<unknown> => {
      try {
        const resolved = await replaceWithResolvedRoute(router, location) as RouteLocationNormalized
        useRedirection().clear()
        if (!resolved?.matched?.length || !resolved.name) {
          logger.error('redirection failed!', r)
          return defaultRedirect?.({ onboardingFinished })
        }
        logger.debug(`target url found! >>> ${String(resolved.name ?? '')}`)
        return resolved
      } catch {
        useRedirection().clear()
        logger.error('redirection failed!', r)
        return defaultRedirect?.({ onboardingFinished })
      }
    })()
  }
}
